import React, { useEffect, useState } from 'react';
import InsightDashboard from './insight-dashboard';
import InsightTable from './insight-table';
import PageHeader from 'components/base/page-header';
import useDashboardContext from 'contexts/dashboard.context';
import { apiService } from 'services/base.service';
import { currentMonth } from 'utils/functions';
import { useSearchParams } from 'react-router-dom';
import { BreakdownDataParamsType, MonthlyBreakDownDataType } from 'models/statements.model';
import {
  DateRangeSelector,
  DateRangeType,
  ENUM_DATE_RANGE,
  getDateRangeSelector
} from '@cokitchen/cokitchen-components';

const MonthlyInsight = () => {
  const { activeCompany } = useDashboardContext();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<MonthlyBreakDownDataType[]>([]);
  const [filteredData, setFilteredData] = useState<MonthlyBreakDownDataType[]>([]);
  const companyId: string = activeCompany?.id;
  const [spender, setSpender] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [pageCount, setPageCount] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParams] = useSearchParams();
  const range = (searchParams.get('range') || ENUM_DATE_RANGE.THIS_WEEK) as ENUM_DATE_RANGE;
  const [dateRange, setDateRange] = useState<DateRangeType>({
    date: getDateRangeSelector(range),
    range
  });

  const start_date = dateRange.date.startDate.toISOString();
  const end_date = dateRange.date.endDate.toISOString();

  const getMonthlyBreakdownData = async () => {
    if (!companyId) return;
    setLoading(true);
    const params: BreakdownDataParamsType = {
      start_date: start_date,
      end_date: end_date,
      company_id: companyId,
      page: pageNumber
    };

    if (departmentId) params.department_id = departmentId;
    if (spender) params.spender = spender;
    try {
      const res = await apiService.get('admin/monthly-breakdown-data', {
        params
      });
      const { data } = res.data;
      setData(data[0].transactions || []);
      setFilteredData(data[0].transactions || []);
      setPageCount(data[0].meta.total_pages);
    } finally {
      setLoading(false);
    }
  };

  const getSelectedPage = async (val: { selected: number }) => {
    const pageNo = val?.selected + 1;
    setPageNumber(pageNo);
  };

  useEffect(() => {
    getMonthlyBreakdownData();
  }, [activeCompany, spender, departmentId, pageNumber, dateRange]);

  useEffect(() => {
    getSelectedPage({ selected: 0 });
  }, []);

  return (
    <div>
      <div className='flex items-start justify-between gap-4'>
        <PageHeader
          title={`${dateRange.range ?? currentMonth}'s breakdown`}
          subtitle='Monitor your spending here'
        />
        <DateRangeSelector defaultRange={dateRange.range} onChange={setDateRange} />
      </div>

      <InsightDashboard dateRange={dateRange} />
      <InsightTable
        loading={loading}
        data={data}
        filteredData={filteredData}
        setFilteredData={setFilteredData}
        setSpender={setSpender}
        setDepartmentId={setDepartmentId}
        pageCount={pageCount}
        getSelectedPage={getSelectedPage}
      />
    </div>
  );
};

export default MonthlyInsight;
